import { ExternalLinkIcon } from '@chakra-ui/icons';
import {
    Avatar,
    Badge,
    Box,
    Link as ChakraLink,
    Heading, HStack,
    IconButton,
    Image, Modal,
    ModalBody, ModalCloseButton,
    ModalContent,
    ModalOverlay,
    SimpleGrid,
    Spinner,
    Text,
    useDisclosure,
    VStack
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { MdContentCopy } from 'react-icons/md';
import { Link, useParams } from 'react-router-dom';
import { getPatientById } from '../../../api';

export default function PatientProfile() {
    const { id } = useParams();
    const [patient, setPatient] = useState(null);
    const [previewSrc, setPreviewSrc] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();

    useEffect(() => {
        (async () => {
            const res = await getPatientById(id);
            if (res?.success) setPatient(res.data);
        })();
    }, [id]);

    if (!patient) return <HStack p={8}><Spinner size="sm" /><Text>Loading…</Text></HStack>;

    const fullName = [patient.firstName, patient.middleName, patient.lastName].filter(Boolean).join(' ');
    const age = patient.dob ? dayjs().diff(dayjs(patient.dob), 'year') : null;
    const idExp = patient.governmentId?.expiration ? dayjs(patient.governmentId.expiration) : null;
    const idExpired = idExp ? idExp.isBefore(dayjs(), 'day') : false;

    const openPreview = (src) => { setPreviewSrc(src); onOpen(); };

    return (
        <Box p={8}>
            {/* Header */}
            <HStack spacing={6} mb={8} align="start">
                <Avatar size="xl" name={fullName} />
                <VStack align="start" spacing={1}>
                    <Heading size="lg" textTransform="capitalize">{fullName || 'Unnamed Patient'}</Heading>
                    <HStack>
                        <Text fontSize="sm" color="gray.500">ID: {patient._id}</Text>
                        <IconButton
                            size="xs"
                            variant="ghost"
                            icon={<MdContentCopy />}
                            aria-label="Copy ID"
                            onClick={() => navigator.clipboard.writeText(patient._id)}
                        />
                    </HStack>
                    {patient.deletedAt && <Badge colorScheme="red">Deleted</Badge>}
                </VStack>
            </HStack>

            <SimpleGrid templateColumns={{ base: '1fr', md: '2fr 1fr' }} spacing={8}>
                <Box>
                    {/* Identity & Contact */}
                    <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                        <Heading size="md" mb={4}>Identity & Contact</Heading>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                            <Text><b>First Name:</b> {patient.firstName || 'N/A'}</Text>
                            <Text><b>Middle Name:</b> {patient.middleName || '—'}</Text>
                            <Text><b>Last Name:</b> {patient.lastName || 'N/A'}</Text>
                            <Text><b>DOB:</b> {patient.dob ? new Date(patient.dob).toLocaleDateString('en-US', { timeZone: 'UTC' }) : 'N/A'}</Text>
                            <Text><b>Age:</b> {age ?? 'N/A'}</Text>
                            <Text><b>Sex:</b> {patient.sex || 'N/A'}</Text>
                            <Text><b>Phone:</b> {patient.phone || 'N/A'}</Text>
                            <Text><b>Email:</b> {patient.email || 'N/A'}</Text>
                        </SimpleGrid>
                    </Box>

                    {/* Government ID */}
                    <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                        <Heading size="md" mb={4}>Government ID</Heading>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
                            <Text><b>ID Number:</b> {patient.governmentId?.idNumber || 'N/A'}</Text>
                            <Text><b>Issuing State:</b> {patient.governmentId?.issuingState}</Text>
                            <Text>
                                <b>Expiration:</b> {idExp ? new Date(idExp).toLocaleDateString('en-US', { timeZone: 'UTC' }) : "N/A"}


                                {idExp && (
                                    <Badge ml={2} colorScheme={idExpired ? 'red' : 'green'}>
                                        {idExpired ? 'Expired' : 'Valid'}
                                    </Badge>
                                )}
                            </Text>
                        </SimpleGrid>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                            <Box>
                                <Text mb={2}><b>Front:</b></Text>
                                {patient.governmentId?.frontImageUrl
                                    ? <Image
                                        src={patient.governmentId.frontImageUrl}
                                        alt="ID Front"
                                        borderRadius="md"
                                        cursor="zoom-in"
                                        onClick={() => openPreview(patient.governmentId.frontImageUrl)}
                                    />
                                    : <Text color="gray.500">N/A</Text>}
                            </Box>
                            <Box>
                                <Text mb={2}><b>Back:</b></Text>
                                {patient.governmentId?.backImageUrl
                                    ? <Image
                                        src={patient.governmentId.backImageUrl}
                                        alt="ID Back"
                                        borderRadius="md"
                                        cursor="zoom-in"
                                        onClick={() => openPreview(patient.governmentId.backImageUrl)}
                                    />
                                    : <Text color="gray.500">N/A</Text>}
                            </Box>
                        </SimpleGrid>
                    </Box>

                    {/* Insurance (Primary) */}
                    <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                        <Heading size="md" mb={4}>Insurance (Primary)</Heading>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
                            <Text><b>Payer:</b> {patient.insurancePrimary?.payerName || 'N/A'}</Text>
                            <Text><b>Plan:</b> {patient.insurancePrimary?.planName || 'N/A'}</Text>
                            <Text><b>Member ID:</b> {patient.insurancePrimary?.memberId || 'N/A'}</Text>
                            <Text><b>Group No:</b> {patient.insurancePrimary?.groupNo || 'N/A'}</Text>
                            <Text><b>Subscriber:</b> {patient.insurancePrimary?.subscriber || 'N/A'}</Text>
                            <Text><b>Relationship:</b> {patient.insurancePrimary?.relationship || 'N/A'}</Text>
                            <Text><b>Effective At:</b> {patient.insurancePrimary?.effectiveAt ? new Date(patient.insurancePrimary?.effectiveAt).toLocaleDateString('en-US', { timeZone: 'UTC' }) : "N/A"}</Text>
                        </SimpleGrid>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                            <Box>
                                <Text mb={2}><b>Card Front:</b></Text>
                                {patient.insurancePrimary?.cardFrontUrl
                                    ? <Image
                                        src={patient.insurancePrimary.cardFrontUrl}
                                        alt="Insurance Front"
                                        borderRadius="md"
                                        cursor="zoom-in"
                                        onClick={() => openPreview(patient.insurancePrimary.cardFrontUrl)}
                                    />
                                    : <Text color="gray.500">N/A</Text>}
                            </Box>
                            <Box>
                                <Text mb={2}><b>Card Back:</b></Text>
                                {patient.insurancePrimary?.cardBackUrl
                                    ? <Image
                                        src={patient.insurancePrimary.cardBackUrl}
                                        alt="Insurance Back"
                                        borderRadius="md"
                                        cursor="zoom-in"
                                        onClick={() => openPreview(patient.insurancePrimary.cardBackUrl)}
                                    />
                                    : <Text color="gray.500">N/A</Text>}
                            </Box>
                        </SimpleGrid>
                    </Box>

                    {/* Metadata */}
                    <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                        <Heading size="md" mb={4}>Metadata</Heading>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                            <Text><b>Created At:</b> {patient.createdAt ? new Date(patient.createdAt).toLocaleString() : 'N/A'}</Text>
                            <Text><b>Updated At:</b> {patient.updatedAt ? new Date(patient.updatedAt).toLocaleString() : 'N/A'}</Text>
                            <Text><b>Deleted At:</b> {patient.deletedAt ? new Date(patient.deletedAt).toLocaleString() : '—'}</Text>
                        </SimpleGrid>
                    </Box>
                </Box>

                {/* Sidebar: Facility */}
                <Box p={6} borderWidth="1px" borderRadius="lg" h="fit-content">
                    <Heading size="md" mb={4}>Facility</Heading>
                    {patient.facility ? (
                        <VStack align="start" spacing={2}>
                            <Text><b>Facility:</b> {patient?.facility?.name}</Text>
                            <ChakraLink
                                as={Link}
                                to={`/facilities/${patient?.facility?._id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                color="blue.500"
                            >
                                View Facility <ExternalLinkIcon mx="2px" />
                            </ChakraLink>
                        </VStack>
                    ) : (
                        <Text color="gray.500">No facility linked.</Text>
                    )}
                </Box>
            </SimpleGrid>

            {/* Image Preview Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
                <ModalOverlay />
                <ModalContent bg="black">
                    <ModalCloseButton color="white" />
                    <ModalBody p={0} display="flex" alignItems="center" justifyContent="center">
                        {previewSrc && (
                            <Image src={previewSrc} alt="Preview" maxH="85vh" maxW="100%" objectFit="contain" />
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
}
